// Build a short, email-ready plain-text summary from existing assessment data.
// Uses only fields already surfaced in the report — no hidden debug fields, no
// backend calls, no PII beyond what the provider snapshot already displays.

const DASH = "\u2014";
const FALLBACK_SOURCE_LINE =
  "SIL-specific source basis confirmed against current publications on 2026-07-05, where applicable.";

const DISCLAIMER =
  "This summary is for consultancy review only. It does not guarantee NDIS registration, compliance, audit, payment, funding, or provider outcomes.";

function safe(value) {
  if (value === undefined || value === null) return DASH;
  const s = String(value).trim();
  return s.length > 0 ? s : DASH;
}

function buildKeyPoints(result) {
  const points = [];
  if (!result) return points;

  if (result.manualReview) {
    points.push("Manual Review Required — automated categorisation is on hold pending consultant review.");
    const firstReasons = (result.manualReviewReasons || []).slice(0, 2);
    for (const reason of firstReasons) {
      const label = typeof reason === "string" ? reason : reason?.label;
      if (label) points.push(`Trigger: ${label}`);
    }
    return points;
  }

  const missing = Array.isArray(result.missingEvidence) ? result.missingEvidence.length : 0;
  const review = Array.isArray(result.reviewEvidence) ? result.reviewEvidence.length : 0;
  const highRisk = Array.isArray(result.operationalRisks)
    ? result.operationalRisks.filter((r) => r?.level === "high").length
    : 0;

  points.push(`${missing} evidence item${missing === 1 ? "" : "s"} missing or unconfirmed.`);
  points.push(`${review} evidence item${review === 1 ? "" : "s"} needing review.`);
  points.push(`${highRisk} high-priority operational risk area${highRisk === 1 ? "" : "s"}.`);

  const clarifications = Array.isArray(result?.clarificationRequired)
    ? result.clarificationRequired.length
    : 0;
  if (clarifications > 0) {
    points.push(`${clarifications} clarification-required item${clarifications === 1 ? "" : "s"}.`);
  }

  return points;
}

function buildReadinessSummary(result) {
  if (!result) return DASH;
  if (result.manualReview) {
    return "Manual review is in effect — automated categorisation is on hold and the report must not be shared externally until a qualified consultant has completed a guided review.";
  }
  const pathway = result?.classifier?.pathway?.title || result?.category?.title || DASH;
  const domains = Array.isArray(result?.domainRollup) ? result.domainRollup : [];
  const red = domains.filter((d) => d?.rag === "red").length;
  const amber = domains.filter((d) => d?.rag === "amber").length;
  const green = domains.filter((d) => d?.rag === "green").length;
  return `Preliminary pathway "${pathway}". R.E.A.D.Y. domain rollup: ${red} red · ${amber} amber · ${green} green.`;
}

function buildSourceLine(result) {
  const sv = result?.sourceVerification;
  if (!sv) return FALLBACK_SOURCE_LINE;
  if (sv.lastReviewedDate) {
    return `SIL-specific source basis confirmed against current publications on ${sv.lastReviewedDate}, where applicable.`;
  }
  return FALLBACK_SOURCE_LINE;
}

export function buildEmailSummary({ result, provider, assessmentId, generatedAt } = {}) {
  const providerName = safe(provider?.providerName);
  const pathway = safe(result?.classifier?.pathway?.title || result?.category?.title);
  const publicationStatus = safe(result?.sourceVerification?.statusLabel || result?.sourceVerification?.status);
  const summary = buildReadinessSummary(result);
  const keyPoints = buildKeyPoints(result);
  const sourceLine = buildSourceLine(result);

  const lines = [
    "NDIS 0138 Readiness SIL Audit Summary",
    "",
    `Reference: ${safe(assessmentId)}`,
    `Generated: ${safe(generatedAt)}`,
    "",
    `Provider: ${providerName}`,
    `Pathway: ${pathway}`,
    `Publication status: ${publicationStatus}`,
    "",
    "Readiness summary:",
    summary,
    "",
    "Key points:"
  ];
  if (keyPoints.length === 0) {
    lines.push(`- ${DASH}`);
  } else {
    for (const point of keyPoints) lines.push(`- ${point}`);
  }
  lines.push("");
  lines.push("Source basis:");
  lines.push(sourceLine);
  lines.push("");
  lines.push("Important note:");
  lines.push(DISCLAIMER);
  return lines.join("\n");
}
